/**
 * @file Detail-view attachments section rendering and DOM wiring.
 */

import {
    canPreviewAttachmentFile,
    findAttachmentByCurrentPath,
    getAttachmentExtension,
    normalizeAttachmentRecords,
} from '../attachments/attachmentModel.js';
import { openAttachmentPreview } from '../attachments/attachmentPreview.js';
import {
    AUTO_GROWING_TEXTAREA_CLASSES,
    INLINE_EDIT_INPUT_CLASSES,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    bindNativeFieldBehavior,
} from '../forms/nativeFieldStyles.js';
import { openConfirmationModal } from '../ui/confirmationModal.js';
import { escapeHTML } from '../utils/contentUtils.js';
import { formatDateTime } from '../utils/dateUtils.js';
import { getUserDisplayNameById } from '../utils/userDisplay.js';

const ATTACHMENT_ACTION_BUTTON_CLASS = 'standard-action-icon-btn shrink-0';
const ATTACHMENT_DOWNLOAD_BUTTON_CLASS = [
    'inline-flex h-8 w-8 shrink-0 items-center justify-center border-0 bg-transparent p-1',
    'text-gray-400 transition-colors hover:text-blue-600 focus:outline-none focus-visible:text-blue-600',
    'dark:text-gray-400 dark:hover:text-blue-400 dark:focus-visible:text-blue-400',
].join(' ');
const ATTACHMENT_DANGER_BUTTON_CLASS = `${ATTACHMENT_ACTION_BUTTON_CLASS} standard-action-icon-btn--danger`;
const EMPTY_METADATA_VALUE = 'Not recorded';

/**
 * Escapes text for an HTML attribute.
 * @param {string} value Raw value.
 * @returns {string} Escaped value.
 */
function attrValue(value) {
    return escapeHTML(String(value ?? ''))
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Formats a file size for display.
 * @param {number|null} size File size in bytes.
 * @returns {string} Human readable file size.
 */
function formatFileSize(size) {
    if (!Number.isFinite(Number(size)) || Number(size) <= 0) {
        return '';
    }
    const units = ['B', 'KB', 'MB'];
    let value = Number(size);
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const precision = unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(precision)} ${units[unitIndex]}`;
}

/**
 * Returns a Material Symbol for a file category.
 * @param {object} attachment Normalized attachment.
 * @returns {string} Icon name.
 */
function getAttachmentIcon(attachment) {
    switch (attachment.category) {
        case 'image':
            return 'image';
        case 'pdf':
            return 'picture_as_pdf';
        case 'word':
            return 'description';
        case 'spreadsheet':
            return 'table';
        case 'presentation':
            return 'slideshow';
        case 'text':
            return 'article';
        case 'archive':
            return 'folder_zip';
        case 'email':
            return 'mail';
        default:
            return 'draft';
    }
}

/**
 * Returns file metadata fragments for a row.
 * @param {object} file Current attachment version.
 * @returns {Array<string>} Metadata fragments.
 */
function getFileMetadataParts(file) {
    const parts = [];
    const extension = getAttachmentExtension(file).replace('.', '').toUpperCase();
    const size = formatFileSize(file.size);
    const uploadedBy = file.uploadedBy ? getUserDisplayNameById(file.uploadedBy) : '';
    const uploadedAt = formatDateTime(file.uploadedAt);

    if (extension) {parts.push(extension);}
    if (size) {parts.push(size);}
    if (uploadedBy || uploadedAt) {
        parts.push(`Uploaded ${uploadedBy ? `by ${uploadedBy}` : ''}${uploadedAt ? ` on ${uploadedAt}` : ''}`.trim());
    }

    return parts;
}

/**
 * Returns the desktop-visible uploaded-by/on line.
 * @param {object} file Current attachment version.
 * @returns {string} Uploaded metadata line.
 */
function getUploadedLine(file) {
    const uploadedBy = file.uploadedBy ? getUserDisplayNameById(file.uploadedBy) : '';
    const uploadedAt = formatDateTime(file.uploadedAt);
    return uploadedBy || uploadedAt ?
        `Uploaded ${uploadedBy ? `by ${uploadedBy}` : ''}${uploadedAt ? ` on ${uploadedAt}` : ''}`.trim() :
        '';
}

/**
 * Returns structured metadata rows for the attachment info modal.
 * @param {object} attachment Normalized attachment.
 * @returns {Array<{label: string, value: string}>} Metadata rows.
 */
function getAttachmentInfoRows(attachment) {
    const file = attachment.currentVersion;
    const extension = getAttachmentExtension(file).replace('.', '').toUpperCase();
    const size = formatFileSize(file.size);
    const uploadedBy = file.uploadedBy ? getUserDisplayNameById(file.uploadedBy) : '';
    const uploadedAt = formatDateTime(file.uploadedAt);
    const editedBy = attachment.editedBy ? getUserDisplayNameById(attachment.editedBy) : '';
    const editedAt = formatDateTime(attachment.editedAt);
    const versionCount = (attachment.versions || []).length || 1;

    return [
        {label: 'Display name', value: attachment.displayName || file.name || EMPTY_METADATA_VALUE},
        {label: 'Original file', value: file.name || EMPTY_METADATA_VALUE},
        {label: 'Versions', value: `${versionCount} ${versionCount === 1 ? 'version' : 'versions'}`},
        {label: 'Type', value: extension || file.type || EMPTY_METADATA_VALUE},
        {label: 'Size', value: size || EMPTY_METADATA_VALUE},
        {label: 'Uploaded by', value: uploadedBy || EMPTY_METADATA_VALUE},
        {label: 'Uploaded on', value: uploadedAt || EMPTY_METADATA_VALUE},
        {label: 'Edited by', value: editedBy || EMPTY_METADATA_VALUE},
        {label: 'Edited on', value: editedAt || EMPTY_METADATA_VALUE},
    ];
}

/**
 * Returns an existing attachment info modal or creates one.
 * @returns {HTMLElement} Modal element.
 */
function getAttachmentInfoModal() {
    let modal = document.getElementById('attachment-info-modal');
    if (modal) {return modal;}

    modal = document.createElement('div');
    modal.id = 'attachment-info-modal';
    modal.className = [
        'fixed',
        'inset-0',
        'z-[80]',
        'hidden',
        'items-center',
        'justify-center',
        'bg-black/50',
        'p-4',
    ].join(' ');
    modal.innerHTML = `
        <div class="w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
            <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 id="attachment-info-title" class="min-w-0 truncate text-base font-semibold text-gray-900 dark:text-gray-100">Attachment info</h3>
                <button type="button" id="attachment-info-close" class="${ATTACHMENT_DANGER_BUTTON_CLASS}" aria-label="Close attachment info">
                    <span class="material-symbols-outlined text-[20px] leading-none">close</span>
                </button>
            </div>
            <div id="attachment-info-body" class="p-4"></div>
        </div>
    `;
    document.body.appendChild(modal);

    const closeModal = () => {
        modal.classList.add('hidden');
        modal.classList.remove('flex');
    };
    modal.querySelector('#attachment-info-close')?.addEventListener('click', closeModal);

    return modal;
}

/**
 * Opens the attachment info modal with wrapped metadata.
 * @param {object} attachment Normalized attachment.
 * @returns {void}
 */
function openAttachmentInfoModal(attachment) {
    const modal = getAttachmentInfoModal();
    const file = attachment.currentVersion || {};
    const title = attachment.displayName || file.name || 'Attachment info';
    const rows = getAttachmentInfoRows(attachment);

    const titleElement = modal.querySelector('#attachment-info-title');
    const bodyElement = modal.querySelector('#attachment-info-body');
    if (titleElement) {
        titleElement.textContent = 'Attachment info';
    }
    if (bodyElement) {
        bodyElement.innerHTML = `
            <p class="mb-3 break-words text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">${escapeHTML(title)}</p>
            <dl class="space-y-2">
                ${rows.map(({label, value}) => `
                    <div class="grid grid-cols-[96px_minmax(0,1fr)] gap-3 text-sm">
                        <dt class="text-gray-500 dark:text-gray-400">${escapeHTML(label)}</dt>
                        <dd class="min-w-0 break-words text-gray-800 dark:text-gray-100">${escapeHTML(value)}</dd>
                    </div>
                `).join('')}
            </dl>
        `;
    }

    modal.classList.remove('hidden');
    modal.classList.add('flex');
}

/**
 * Renders a single attachment row.
 * @param {object} attachment Normalized attachment.
 * @returns {string} Row HTML.
 */
function renderAttachmentRow(attachment) {
    const file = attachment.currentVersion;
    const fileName = attachment.displayName || file.name || 'Attachment';
    const description = attachment.description || '';
    const canPreview = canPreviewAttachmentFile(file);
    const metadataLabel = getFileMetadataParts(file).join(', ') || 'Attachment details';
    const uploadedLine = getUploadedLine(file);
    const olderVersions = (attachment.versions || [])
        .filter((version) => version?.id !== attachment.currentVersionId)
        .reverse();
    const versionHistoryHtml = olderVersions.length ? `
        <details class="attachment-version-history mt-2">
            <summary class="inline-flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-[var(--dfc-brand-primary)] dark:text-gray-400 dark:hover:text-[var(--dfc-brand-primary)]">
                <span class="material-symbols-outlined text-[17px] leading-none">history</span>
                ${olderVersions.length} older ${olderVersions.length === 1 ? 'version' : 'versions'}
            </summary>
            <ul class="mt-1 space-y-1 border-l border-gray-200 pl-3 dark:border-gray-700">
                ${olderVersions.map((version) => {
                    const canPreviewVersion = canPreviewAttachmentFile(version);
                    const versionMeta = [
                        getAttachmentExtension(version).replace('.', '').toUpperCase(),
                        formatFileSize(version.size),
                        getUploadedLine(version),
                    ].filter(Boolean).join(' · ');
                    return `
                        <li class="attachment-version-row rounded px-2 py-1 text-gray-500 dark:text-gray-400"
                            data-version-id="${attrValue(version.id)}"
                            data-version-path="${attrValue(version.path)}">
                            <div class="flex min-w-0 items-start justify-between gap-2">
                                <div class="min-w-0">
                                    <p class="truncate text-xs font-medium">${escapeHTML(version.name || 'Attachment version')}</p>
                                    ${versionMeta ? `<p class="truncate text-[11px] leading-4">${escapeHTML(versionMeta)}</p>` : ''}
                                </div>
                                <div class="flex shrink-0 items-center gap-0.5">
                                    ${canPreviewVersion ? `<button type="button" class="attachment-version-open-btn ${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Open version" aria-label="Open version">
                                        <span class="material-symbols-outlined text-[18px] leading-none">open_in_full</span>
                                    </button>` : ''}
                                    <a href="${attrValue(version.url)}" target="_blank" rel="noopener" class="${ATTACHMENT_DOWNLOAD_BUTTON_CLASS}" title="Download version" aria-label="Download version">
                                        <span class="material-symbols-outlined text-[18px] leading-none">download</span>
                                    </a>
                                    <button type="button" class="attachment-version-delete-btn ${ATTACHMENT_DANGER_BUTTON_CLASS}" title="Delete version" aria-label="Delete version">
                                        <span class="material-symbols-outlined text-[18px] leading-none">delete</span>
                                    </button>
                                </div>
                            </div>
                        </li>
                    `;
                }).join('')}
            </ul>
        </details>
    ` : '';

    return `
        <li class="attachment-row rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-800"
            data-attachment-id="${attrValue(attachment.id)}"
            data-file-path="${attrValue(file.path)}">
            <div class="grid min-w-0 grid-cols-[20px_minmax(0,1fr)] gap-x-2 sm:grid-cols-[20px_minmax(0,1fr)_auto]">
                <label class="attachment-select-cell ${NATIVE_CHECKBOX_LABEL_CLASSES} col-start-1 row-start-1 hidden h-[22px] min-h-0 items-center justify-center gap-0" data-native-checkbox-label>
                    <input type="checkbox" class="attachment-select-checkbox ${NATIVE_CHECKBOX_CLASSES}" aria-label="Select attachment">
                    <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                    </span>
                </label>
                <span class="attachment-file-icon material-symbols-outlined col-start-1 row-start-1 flex h-[22px] items-center justify-center text-[20px] leading-none text-gray-500 dark:text-gray-400">${getAttachmentIcon(attachment)}</span>
                <div class="col-start-2 row-start-1 min-w-0">
                    <label class="sr-only" for="attachment-name-${attrValue(attachment.id)}">Attachment name</label>
                    <textarea
                        id="attachment-name-${attrValue(attachment.id)}"
                        class="attachment-name-input ${INLINE_EDIT_INPUT_CLASSES} min-h-[22px] resize-none overflow-hidden whitespace-normal break-words !py-px font-semibold leading-5 text-gray-900 dark:text-gray-100"
                        rows="1"
                        data-auto-grow="true"
                        data-original-value="${attrValue(fileName)}"
                        aria-label="Attachment name"
                    >${escapeHTML(fileName)}</textarea>
                    ${uploadedLine ? `<p class="-mt-px hidden text-[11px] leading-4 text-gray-400 dark:text-gray-500 sm:block">${escapeHTML(uploadedLine)}</p>` : ''}
                    ${description ? `<p class="attachment-description-summary whitespace-pre-wrap break-words text-xs leading-5 text-gray-500 dark:text-gray-400">${escapeHTML(description)}</p>` : ''}
                    <div class="attachment-description-editor mt-2 hidden">
                        <textarea
                            class="attachment-description-input ${AUTO_GROWING_TEXTAREA_CLASSES}"
                            rows="1"
                            data-auto-grow="true"
                            data-original-value="${attrValue(description)}"
                            placeholder="Add description"
                            aria-label="Attachment description"
                        >${escapeHTML(description)}</textarea>
                    </div>
                    ${versionHistoryHtml}
                </div>
                <div class="relative col-start-2 row-start-2 mt-0.5 flex shrink-0 flex-col items-end self-start sm:col-start-3 sm:row-start-1 sm:-mt-1">
                    <div class="flex items-center justify-end gap-0.5">
                        ${canPreview ? `<button type="button" class="attachment-open-btn ${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Open attachment" aria-label="Open attachment">
                            <span class="material-symbols-outlined text-[19px] leading-none">open_in_full</span>
                        </button>` : ''}
                        <a href="${attrValue(file.url)}" target="_blank" rel="noopener" class="${ATTACHMENT_DOWNLOAD_BUTTON_CLASS}" title="Download attachment" aria-label="Download attachment">
                            <span class="material-symbols-outlined text-[19px] leading-none">download</span>
                        </a>
                        <button type="button" class="attachment-description-toggle-btn ${ATTACHMENT_ACTION_BUTTON_CLASS}" title="${description ? 'Edit description' : 'Add description'}" aria-label="${description ? 'Edit description' : 'Add description'}">
                            <span class="material-symbols-outlined text-[19px] leading-none">notes</span>
                        </button>
                        <button type="button" class="attachment-upload-version-btn ${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Upload new version" aria-label="Upload new version">
                            <span class="material-symbols-outlined text-[19px] leading-none">upload</span>
                        </button>
                        <button type="button" class="attachment-link-btn ${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Copy attachment link" aria-label="Copy attachment link">
                            <span class="material-symbols-outlined text-[19px] leading-none">link</span>
                        </button>
                        <button type="button" class="attachment-info-btn ${ATTACHMENT_ACTION_BUTTON_CLASS}" title="${attrValue(metadataLabel)}" aria-label="Attachment info">
                            <span class="material-symbols-outlined text-[19px] leading-none">info</span>
                        </button>
                        <button type="button" class="delete-file-btn ${ATTACHMENT_DANGER_BUTTON_CLASS}" title="Delete attachment" aria-label="Delete attachment" data-file-path="${attrValue(file.path)}">
                            <span class="material-symbols-outlined text-[19px] leading-none">delete</span>
                        </button>
                    </div>
                    <span class="attachment-link-feedback mt-0.5 hidden pr-1 text-[11px] font-medium leading-3 text-[var(--dfc-brand-accent)] sm:absolute sm:right-1 sm:top-[30px] sm:mt-0 sm:pr-0">Link copied</span>
                </div>
            </div>
        </li>
    `;
}

/**
 * Renders the Attachments section within the details view.
 * @param {object} item Item with an attachments array.
 * @returns {string} Attachments section HTML.
 */
export function renderAttachmentsSection(item) {
    if (!item || !item.id) {return '';}

    const attachments = normalizeAttachmentRecords(item.attachments || []);
    const attachmentsHtml = attachments.length > 0 ?
        `<ul class="space-y-2">${attachments.map(renderAttachmentRow).join('')}</ul>` :
        `<div class="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No attachments yet.</div>`;

    return `
        <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 mt-6">
            <div class="mb-4 flex items-center justify-between gap-3">
                <h2 class="text-xl font-semibold dark:text-gray-100">Attachments</h2>
                <div class="flex items-center gap-2">
                    ${attachments.length > 0 ? `<button type="button" id="attachment-bulk-toggle" class="${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Select attachments" aria-label="Select attachments">
                        <span class="material-symbols-outlined text-[21px] leading-none">checklist</span>
                    </button>` : ''}
                    <button type="button" id="attachment-upload-button" class="${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Upload attachment" aria-label="Upload attachment">
                        <span class="material-symbols-outlined text-[21px] leading-none">upload</span>
                    </button>
                    <input id="file-upload" name="file-upload" type="file" class="sr-only" multiple />
                    <input id="attachment-version-upload" name="attachment-version-upload" type="file" class="sr-only" />
                </div>
            </div>
            <div id="attachment-bulk-bar" class="mb-2 hidden items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
                <span id="attachment-bulk-count" class="text-xs font-medium text-gray-600 dark:text-gray-300">0 selected</span>
                <div class="flex items-center gap-1">
                    <button type="button" id="attachment-bulk-download" class="${ATTACHMENT_DOWNLOAD_BUTTON_CLASS}" title="Download selected" aria-label="Download selected" disabled>
                        <span class="material-symbols-outlined text-[19px] leading-none">download</span>
                    </button>
                    <button type="button" id="attachment-bulk-delete" class="${ATTACHMENT_DANGER_BUTTON_CLASS}" title="Delete selected" aria-label="Delete selected" disabled>
                        <span class="material-symbols-outlined text-[19px] leading-none">delete</span>
                    </button>
                    <button type="button" id="attachment-bulk-cancel" class="${ATTACHMENT_ACTION_BUTTON_CLASS}" title="Cancel selection" aria-label="Cancel selection">
                        <span class="material-symbols-outlined text-[19px] leading-none">close</span>
                    </button>
                </div>
            </div>
            <div id="attachment-drop-zone" class="relative rounded-lg">
                <div id="attachment-upload-status" class="mb-2 hidden rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
                    <div class="flex items-start gap-2">
                        <span id="attachment-upload-status-icon" class="material-symbols-outlined mt-0.5 shrink-0 text-[19px] leading-none text-gray-500 dark:text-gray-400">upload_file</span>
                        <div class="min-w-0 flex-1">
                            <div class="flex min-w-0 items-center justify-between gap-3">
                                <p id="attachment-upload-status-title" class="min-w-0 truncate text-sm font-semibold text-gray-800 dark:text-gray-100">Uploading attachment</p>
                                <span id="attachment-upload-status-percent" class="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">0%</span>
                            </div>
                            <p id="attachment-upload-status-detail" class="mt-0.5 min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">Preparing upload</p>
                            <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                <div id="attachment-upload-status-fill" class="h-full rounded-full bg-[var(--dfc-brand-primary)] transition-[width] duration-150" style="width: 0%;"></div>
                            </div>
                        </div>
                    </div>
                </div>
                ${attachmentsHtml}
                <div id="attachment-drag-overlay" class="pointer-events-none absolute inset-0 hidden items-center justify-center rounded-lg border-2 border-dashed border-[var(--dfc-brand-primary)] bg-white/80 text-lg font-semibold text-gray-600 dark:bg-gray-900/80 dark:text-gray-300">
                    Drop files to upload
                </div>
            </div>
        </div>
    `;
}

/**
 * Attaches upload, drag/drop, metadata, preview, and delete listeners.
 * @param {HTMLElement} detailsViewContainer Detail view container.
 * @param {object} item Current detail item.
 * @param {object} callbacks Listener callbacks.
 * @param {Function} callbacks.handleAttachmentBulkDelete Bulk delete handler.
 * @param {Function} callbacks.handleAttachmentBulkDownload Bulk download handler.
 * @param {Function} callbacks.handleAttachmentLinkCopy Link copy handler.
 * @param {Function} callbacks.handleAttachmentMetadataSave Metadata save handler.
 * @param {Function} callbacks.handleAttachmentOfficePreview Office preview handler.
 * @param {Function} callbacks.handleAttachmentVersionUpload Version upload handler.
 * @param {Function} callbacks.handleFileUpload Existing upload handler.
 * @param {Function} callbacks.handleFileDelete Existing delete handler.
 */
export function attachAttachmentsSectionListeners(
    detailsViewContainer,
    item,
    {
        handleAttachmentBulkDelete,
        handleAttachmentBulkDownload,
        handleAttachmentLinkCopy,
        handleAttachmentMetadataSave,
        handleAttachmentOfficePreview,
        handleAttachmentVersionUpload,
        handleFileUpload,
        handleFileDelete,
    },
) {
    const fileUploadInput = detailsViewContainer.querySelector('#file-upload');
    const versionUploadInput = detailsViewContainer.querySelector('#attachment-version-upload');
    const uploadButton = detailsViewContainer.querySelector('#attachment-upload-button');
    const dropZone = detailsViewContainer.querySelector('#attachment-drop-zone');
    const dragOverlay = detailsViewContainer.querySelector('#attachment-drag-overlay');
    const attachments = normalizeAttachmentRecords(item.attachments || []);
    const bulkToggle = detailsViewContainer.querySelector('#attachment-bulk-toggle');
    const bulkBar = detailsViewContainer.querySelector('#attachment-bulk-bar');
    const bulkCount = detailsViewContainer.querySelector('#attachment-bulk-count');
    const bulkDownload = detailsViewContainer.querySelector('#attachment-bulk-download');
    const bulkDelete = detailsViewContainer.querySelector('#attachment-bulk-delete');
    const bulkCancel = detailsViewContainer.querySelector('#attachment-bulk-cancel');
    const selectedAttachmentIds = new Set();

    let linkFeedbackTimeout = null;

    const getSelectedAttachments = () => attachments.filter((attachment) => {
        return selectedAttachmentIds.has(attachment.id);
    });

    const updateSelectedRowStyle = (row, selected) => {
        row.classList.toggle('bg-white', !selected);
        row.classList.toggle('dark:bg-gray-800', !selected);
        row.classList.toggle('bg-gray-50', selected);
        row.classList.toggle('dark:bg-gray-700', selected);
    };

    const updateBulkUi = () => {
        const selectedCount = selectedAttachmentIds.size;
        if (bulkCount) {
            bulkCount.textContent = `${selectedCount} selected`;
        }
        [bulkDownload, bulkDelete].forEach((button) => {
            if (button) {
                button.disabled = selectedCount === 0;
                button.classList.toggle('opacity-50', selectedCount === 0);
            }
        });
    };

    const setBulkMode = (active) => {
        bulkBar?.classList.toggle('hidden', !active);
        bulkBar?.classList.toggle('flex', active);
        detailsViewContainer.querySelectorAll('.attachment-row').forEach((row) => {
            const checkbox = row.querySelector('.attachment-select-checkbox');
            row.querySelector('.attachment-select-cell')?.classList.toggle('hidden', !active);
            row.querySelector('.attachment-select-cell')?.classList.toggle('flex', active);
            if (checkbox) {checkbox.checked = false;}
            row.querySelector('.attachment-file-icon')?.classList.toggle('hidden', active);
            updateSelectedRowStyle(row, false);
        });
        selectedAttachmentIds.clear();
        updateBulkUi();
    };

    if (uploadButton && fileUploadInput) {
        uploadButton.onclick = () => fileUploadInput.click();
    }
    if (fileUploadInput) {
        fileUploadInput.onchange = handleFileUpload;
    }
    if (bulkToggle) {
        bulkToggle.onclick = () => setBulkMode(bulkBar?.classList.contains('hidden') === true);
    }
    if (bulkCancel) {
        bulkCancel.onclick = () => setBulkMode(false);
    }
    if (bulkDownload) {
        bulkDownload.onclick = async () => {
            await handleAttachmentBulkDownload(Array.from(selectedAttachmentIds));
            setBulkMode(false);
        };
    }
    if (bulkDelete) {
        bulkDelete.onclick = () => {
            const selectedAttachments = getSelectedAttachments();
            if (!selectedAttachments.length) {return;}
            openConfirmationModal(
                'Delete Selected Attachments?',
                `Delete ${selectedAttachments.length} selected attachment${selectedAttachments.length === 1 ? '' : 's'} and all saved versions? This action cannot be undone.`,
                async () => {
                    await handleAttachmentBulkDelete(selectedAttachments);
                    setBulkMode(false);
                },
            );
        };
    }
    if (versionUploadInput) {
        versionUploadInput.onchange = async (event) => {
            const attachmentId = versionUploadInput.dataset.attachmentId;
            const attachment = attachments.find((entry) => entry.id === attachmentId);
            const file = event.target?.files?.[0] || null;
            versionUploadInput.value = '';
            if (!attachment || !file) {return;}
            await handleAttachmentVersionUpload({attachment, file});
        };
    }

    const findAttachmentForRow = (row) => {
        const filePath = row?.dataset.filePath;
        return attachments.find((attachment) => {
            return attachment.currentVersion?.path === filePath;
        }) || null;
    };

    const saveMetadata = async (row) => {
        const attachment = findAttachmentForRow(row);
        if (!attachment) {return;}

        const nameInput = row.querySelector('.attachment-name-input');
        const descriptionInput = row.querySelector('.attachment-description-input');
        const displayName = nameInput?.value?.replace(/\s*\n+\s*/g, ' ').trim() || '';
        const description = descriptionInput?.value?.trim() || '';
        const originalName = nameInput?.dataset.originalValue || '';
        const originalDescription = descriptionInput?.dataset.originalValue || '';

        if (displayName === originalName && description === originalDescription) {
            return;
        }

        try {
            row.dataset.saving = 'true';
            await handleAttachmentMetadataSave({
                attachmentId: attachment.id,
                filePath: attachment.currentVersion?.path,
                displayName,
                description,
            });
        } catch (error) {
            console.error('Attachment metadata save failed:', error);
            if (nameInput) {
                nameInput.value = originalName;
            }
            if (descriptionInput) {
                descriptionInput.value = originalDescription;
            }
            openConfirmationModal(
                'Attachment Not Saved',
                escapeHTML(error.message || 'Could not update attachment metadata.'),
                async () => {},
                false,
                'OK',
            );
        } finally {
            delete row.dataset.saving;
        }
    };

    detailsViewContainer.querySelectorAll('.attachment-name-input').forEach((input) => {
        input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                input.blur();
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                input.value = input.dataset.originalValue || '';
                input.dispatchEvent(new Event('input', {bubbles: true}));
                input.blur();
            }
        });
        input.addEventListener('blur', () => saveMetadata(input.closest('.attachment-row')));
    });

    detailsViewContainer.querySelectorAll('.attachment-row').forEach((row) => {
        bindNativeFieldBehavior(row);
    });

    detailsViewContainer.querySelectorAll('.attachment-select-checkbox').forEach((checkbox) => {
        checkbox.onchange = (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const attachmentId = row?.dataset.attachmentId;
            if (!attachmentId) {return;}
            if (event.currentTarget.checked) {
                selectedAttachmentIds.add(attachmentId);
                updateSelectedRowStyle(row, true);
            } else {
                selectedAttachmentIds.delete(attachmentId);
                updateSelectedRowStyle(row, false);
            }
            updateBulkUi();
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-description-toggle-btn').forEach((button) => {
        button.addEventListener('mousedown', (event) => {
            event.preventDefault();
        });
        button.onclick = async (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const editor = row?.querySelector('.attachment-description-editor');
            const input = row?.querySelector('.attachment-description-input');
            const summary = row?.querySelector('.attachment-description-summary');
            if (!editor || !input) {return;}

            const isOpening = editor.classList.contains('hidden');
            if (isOpening) {
                editor.classList.remove('hidden');
                summary?.classList.add('hidden');
                input.focus();
                input.dispatchEvent(new Event('input', {bubbles: true}));
            } else {
                await saveMetadata(row);
                editor.classList.add('hidden');
                summary?.classList.remove('hidden');
            }
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-description-input').forEach((input) => {
        input.addEventListener('blur', async () => {
            const row = input.closest('.attachment-row');
            const editor = row?.querySelector('.attachment-description-editor');
            const summary = row?.querySelector('.attachment-description-summary');
            await saveMetadata(row);
            editor?.classList.add('hidden');
            summary?.classList.remove('hidden');
        });
    });

    detailsViewContainer.querySelectorAll('.attachment-info-btn').forEach((button) => {
        button.onclick = (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const attachment = findAttachmentForRow(row);
            if (!attachment) {return;}
            openAttachmentInfoModal(attachment);
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-link-btn').forEach((button) => {
        button.onclick = async (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const attachment = findAttachmentForRow(row);
            if (!attachment) {return;}
            const link = await handleAttachmentLinkCopy({attachment});
            if (!link) {return;}
            const feedback = row.querySelector('.attachment-link-feedback');
            if (!feedback) {return;}
            if (linkFeedbackTimeout) {
                window.clearTimeout(linkFeedbackTimeout);
            }
            feedback.classList.remove('hidden');
            linkFeedbackTimeout = window.setTimeout(() => {
                feedback.classList.add('hidden');
            }, 1800);
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-upload-version-btn').forEach((button) => {
        button.onclick = (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const attachment = findAttachmentForRow(row);
            if (!attachment || !versionUploadInput) {return;}
            versionUploadInput.dataset.attachmentId = attachment.id;
            versionUploadInput.click();
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-open-btn').forEach((button) => {
        button.onclick = async (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const attachment = findAttachmentForRow(row);
            if (!attachment) {return;}

            try {
                await openAttachmentPreview({
                    attachment,
                    handleAttachmentOfficePreview,
                });
            } catch (error) {
                console.error('Attachment preview failed:', error);
                const fallbackUrl = attachment.currentVersion?.url;
                if (fallbackUrl) {
                    window.open(fallbackUrl, '_blank', 'noopener');
                }
            }
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-version-open-btn').forEach((button) => {
        button.onclick = async (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const versionRow = event.currentTarget.closest('.attachment-version-row');
            const attachment = findAttachmentForRow(row);
            const versionId = versionRow?.dataset.versionId;
            const version = attachment?.versions?.find((entry) => entry.id === versionId);
            if (!attachment || !version) {return;}

            try {
                await openAttachmentPreview({
                    attachment: {
                        ...attachment,
                        currentVersionId: version.id,
                        currentVersion: version,
                    },
                    handleAttachmentOfficePreview,
                });
            } catch (error) {
                console.error('Attachment version preview failed:', error);
                if (version.url) {
                    window.open(version.url, '_blank', 'noopener');
                }
            }
        };
    });

    detailsViewContainer.querySelectorAll('.attachment-version-delete-btn').forEach((button) => {
        button.onclick = (event) => {
            const row = event.currentTarget.closest('.attachment-row');
            const versionRow = event.currentTarget.closest('.attachment-version-row');
            const attachment = findAttachmentForRow(row);
            const versionId = versionRow?.dataset.versionId;
            const version = attachment?.versions?.find((entry) => entry.id === versionId);
            if (!attachment || !version?.path) {return;}

            openConfirmationModal(
                'Delete Version?',
                'Delete this older attachment version? The current version will remain available. This action cannot be undone.',
                () => handleFileDelete(version.path, version, {deleteMode: 'version'}),
            );
        };
    });

    detailsViewContainer.querySelectorAll('.delete-file-btn').forEach((btn) => {
        btn.onclick = (e) => {
            const button = e.currentTarget;
            const filePath = button.dataset.filePath;
            if (!filePath) {return;}

            const attachment = findAttachmentByCurrentPath(item.attachments, filePath);
            if (!attachment) {
                openConfirmationModal(
                    'Error',
                    'File data not found. Cannot delete.',
                    async () => { },
                    false,
                    'OK',
                );
                return;
            }

            openConfirmationModal(
                'Delete Attachment?',
                attachment.versions.length > 1 ?
                    'This attachment has multiple versions. Delete only the current version, or delete all versions?' :
                    'Are you sure you want to delete this attachment? This cannot be undone.',
                () => handleFileDelete(
                    filePath,
                    attachment.currentVersion,
                    {deleteMode: 'attachment'},
                ),
                true,
                attachment.versions.length > 1 ? 'Delete All' : 'Delete',
                null,
                '',
                'Cancel',
                attachment.versions.length > 1 ? {
                    leftAction: {
                        buttonText: 'Delete Current',
                        variant: 'dangerSecondary',
                        placement: 'beforeConfirm',
                        onClick: () => handleFileDelete(
                            filePath,
                            attachment.currentVersion,
                            {deleteMode: 'version'},
                        ),
                    },
                } : {},
            );
        };
    });

    if (!dropZone) {return;}

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
        dropZone.addEventListener(eventName, (e) => {
            e.preventDefault();
            e.stopPropagation();
        }, false);
    });
    ['dragenter', 'dragover'].forEach((eventName) => {
        dropZone.addEventListener(
            eventName,
            () => {
                dragOverlay?.classList.remove('hidden');
                dragOverlay?.classList.add('flex');
            },
            false,
        );
    });
    ['dragleave', 'drop'].forEach((eventName) => {
        dropZone.addEventListener(
            eventName,
            () => {
                dragOverlay?.classList.add('hidden');
                dragOverlay?.classList.remove('flex');
            },
            false,
        );
    });
    dropZone.addEventListener('drop', (e) => {
        const files = e.dataTransfer?.files;
        if (files && files.length > 0 && fileUploadInput) {
            fileUploadInput.files = files;
            handleFileUpload({target: fileUploadInput});
        }
    }, false);
}
